/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetFleetCoverageRequest,
  GetFleetCoverageSecurity,
} from "../models/operations/getfleetcoverage.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildFleetCoverageQuery,
  FleetCoverageQueryData,
  prefetchFleetCoverage,
  queryKeyFleetCoverage,
} from "./fleetCoverage.core.js";
export {
  buildFleetCoverageQuery,
  type FleetCoverageQueryData,
  prefetchFleetCoverage,
  queryKeyFleetCoverage,
};

export type FleetCoverageQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getFleetCoverage deviceIntegrations
 *
 * @remarks
 * Summarize agent coverage and policy enforcement across every device the org can see, with or without a connected MDM, optionally scoped to one provider's inventory.
 */
export function useFleetCoverage(
  request?: GetFleetCoverageRequest | undefined,
  security?: GetFleetCoverageSecurity | undefined,
  options?: QueryHookOptions<FleetCoverageQueryData, FleetCoverageQueryError>,
): UseQueryResult<FleetCoverageQueryData, FleetCoverageQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildFleetCoverageQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getFleetCoverage deviceIntegrations
 *
 * @remarks
 * Summarize agent coverage and policy enforcement across every device the org can see, with or without a connected MDM, optionally scoped to one provider's inventory.
 */
export function useFleetCoverageSuspense(
  request?: GetFleetCoverageRequest | undefined,
  security?: GetFleetCoverageSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    FleetCoverageQueryData,
    FleetCoverageQueryError
  >,
): UseSuspenseQueryResult<FleetCoverageQueryData, FleetCoverageQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildFleetCoverageQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setFleetCoverageData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      provider?: string | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: FleetCoverageQueryData,
): FleetCoverageQueryData | undefined {
  const key = queryKeyFleetCoverage(...queryKeyBase);

  return client.setQueryData<FleetCoverageQueryData>(key, data);
}

export function invalidateFleetCoverage(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      provider?: string | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "deviceIntegrations",
      "getFleetCoverage",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllFleetCoverage(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "deviceIntegrations", "getFleetCoverage"],
  });
}
